import React, { useEffect } from 'react'
import { Form } from 'antd';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import { api } from '../../../../../api/api';
import { ObjDB, TrainingProgramsObj } from '../../../../../types/apiTypes';
import FormStatus from '../../../../../components/form/FormStatus/FormStatus';
import { useFormStatus } from '../../../../../utils/hooks/useFormStatus';
import EditPlaceholder from '../../../../../components/common/EditPlaceholder/EditPlaceholder';
import { getAntField } from '../../../../../utils/form';
import { TrainingProgramsFormFields } from '../../../../../types/formFieldTypes';
import { errorNotification, invalidateQueries } from '../../../../../utils/rest';
import { SwitchTrainingProgramsItems } from '../../../../../types/propsTypes';
import { clearTrainingProgramsErrors, trainingProgramsValidate, initialTrainingProgramsFields } from '../../formData/TrainingProgramsFormData';
import TrainingProgramsFormInputs from '../TrainingProgramsFormInputs/TrainingProgramsFormInputs';
import { queryKeysTrainingPrograms } from '../../../../../static/data/queryKeys';

type TrainingProgramsEditFormProps = {
    editItem: ObjDB<TrainingProgramsObj> | null
    switchData: SwitchTrainingProgramsItems
    setOpenedForm: React.Dispatch<React.SetStateAction<boolean>>
    setEditItem: React.Dispatch<React.SetStateAction<ObjDB<TrainingProgramsObj> | null>>
}

const TrainingProgramsEditForm: React.FC<React.PropsWithChildren<TrainingProgramsEditFormProps>> = React.memo((props) => {
    const {
        editItem, setOpenedForm, setEditItem,
        switchData,
    } = props

    const formStatus = useFormStatus()

    const {
        errors, values, touched,
        setFieldValue, handleChange,
        submitForm, setValues, setErrors,
    } = useFormik<TrainingProgramsFormFields>({
        initialValues: initialTrainingProgramsFields(editItem?.education_course_id || 0),
        onSubmit: (values) => {
            mutate(values)
        },
        validateOnChange: false,
        validate: trainingProgramsValidate
    })

    const antField = getAntField<TrainingProgramsFormFields>(values, errors, touched)

    const {
        mutate
    } = useMutation(
        async (values: TrainingProgramsFormFields) => {
            if (editItem) return await api.trainingPrograms.edit(values, editItem)
        },
        {
            mutationKey: 'training-program-edit',
            onSuccess() {
                invalidateQueries(queryKeysTrainingPrograms[1])
                formStatus.setMessage('Данные успешно изменены')
                formStatus.setStatus('success')
                setTimeout(clearEditItem, 1000)
            },
            onError: (err: Error) => errorNotification(err, 'Ошибка редактирования', formStatus),
            onMutate() {
                formStatus.setMessage('Отправка данных')
                formStatus.setStatus('fetching')
            }
        }
    )

    const clearForm = () => {
        setValues(initialTrainingProgramsFields(0))
        setErrors(clearTrainingProgramsErrors)
    }

    const clearEditItem = () => {
        clearForm()
        setOpenedForm(false)
        setTimeout(() => {
            setEditItem(null)
            formStatus.reset()
        }, 300)
    }

    const resetChanges = () => {
        if (editItem) {
            const { id, ...values } = editItem
            setValues(values)
            setErrors(clearTrainingProgramsErrors)
        }
    }

    useEffect(() => {
        if (editItem) return resetChanges()
        clearForm()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editItem])

    if (!editItem) return <EditPlaceholder />

    return (
        <Form
            fields={[
                antField('education_course_id', {withFalsy: true}),
                antField('title'),
                antField('text'),
                antField('count_hours'),
            ]}
        >
            {
                editItem &&
                <p className="uif-ant-form-title">
                    ID редактируемого объекта: <span>{editItem.id}</span>
                </p>
            }

            <TrainingProgramsFormInputs
                switchData={switchData}
                values={values}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                resetChanges={resetChanges}
                submitForm={submitForm}
            />

            <FormStatus formStatus={formStatus} />
        </Form>
    )
})

export default TrainingProgramsEditForm